import React, { useMemo, useState } from 'react';
import { trackEvent } from 'utils/analytics';
import { SpotInterface } from 'types';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import LinesIcon from 'assets/icons/lines.svg?react';
import WidgetBox from 'components/widgets/WidgetBox';
import AGMessage from 'components/AGMessage/AGMessage';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import Label from 'components/AGForm/Inputs/Label/Label';
import {
    OnOffOption,
    Period,
    periodOptions,
    RevenueType,
    revenueTypeOptions,
    Timeframe,
    timeframeOptions,
} from '../helpers';
import MoMRevenue from './MoMRevenue';
import DoDRevenue from './DoDRevenue';
import styles from '../RevenueWidgets.module.css';
import Toggle from 'components/AGForm/Inputs/Toggle/Toggle';

type MonthlyAndDailyRevenueProps = {
    spot: SpotInterface;
    filters: {
        revenueType: RevenueType;
        timeframe: Timeframe;
        dailyPeriod: Period;
        momYearCompare: OnOffOption;
        momGuaranteeCompare: OnOffOption;
    };
    urlUpdater: UrlUpdaterFunctions<{
        revenueType: RevenueType;
        timeframe: Timeframe;
        dailyPeriod: Period;
        momYearCompare: OnOffOption;
        momGuaranteeCompare: OnOffOption;
    }>;
};

function MonthlyAndDailyRevenue({ spot, filters, urlUpdater }: MonthlyAndDailyRevenueProps): JSX.Element {
    const [errorMessage, setErrorMessage] = useState('');
    const [showYearCompareToggle, setShowYearCompareToggle] = useState(false);
    const [showGuaranteeToggle, setShowGuaranteeToggle] = useState(false);

    const isYearToggleOn = useMemo(
        () => filters.momYearCompare === 'on' && showYearCompareToggle,
        [filters.momYearCompare, showYearCompareToggle]
    );
    const isGuaranteeToggleOn = useMemo(
        () => filters.momGuaranteeCompare === 'on' && showGuaranteeToggle,
        [filters.momGuaranteeCompare, showGuaranteeToggle]
    );

    function onSelectRevenueType(selection: string) {
        urlUpdater.revenueType(selection);
        trackEvent(`Select MoM ${selection}`, { spot: spot.pk, spotName: spot.name });
    }

    function onSelectTimeframe(selection: string) {
        urlUpdater.timeframe(selection);
        trackEvent(`Select new timeframe: ${selection}`, { spot: spot.pk, spotName: spot.name });
    }

    function onSelectDailyPeriod(selection: string) {
        urlUpdater.dailyPeriod(selection);
        trackEvent(`Select new daily period: ${selection}`, { spot: spot.pk, spotName: spot.name });
    }

    // Only one of these two toggles (year or guarantee) can be on at a time.
    function onChangeYearCompareToggle() {
        const selection = !isYearToggleOn ? 'on' : 'off';
        urlUpdater.momYearCompare(selection);
        if (selection === 'on' && isGuaranteeToggleOn) urlUpdater.momGuaranteeCompare('off');
        trackEvent(`Changed Year Comparison toggle: ${selection}`, { spot: spot.pk, spotName: spot.name });
    }

    function onChangeGuaranteeCompareToggle() {
        const selection = !isGuaranteeToggleOn ? 'on' : 'off';
        // Year compare must be turned off *before* guarantee is turned on: urlUpdater validates this
        if (selection === 'on' && isYearToggleOn) urlUpdater.momYearCompare('off');
        urlUpdater.momGuaranteeCompare(selection);
        trackEvent(`Changed Minimum Guarantee toggle: ${selection}`, { spot: spot.pk, spotName: spot.name });
    }

    return (
        <WidgetBox title="Revenue">
            <>
                <div className={styles.filters}>
                    <div>
                        <Label label="Metric" />
                        <AGSimpleSelect
                            selected={filters.revenueType}
                            onSelect={onSelectRevenueType}
                            options={revenueTypeOptions}
                            icon={<LinesIcon />}
                            width="170px"
                        />
                    </div>
                    <div>
                        <Label label="Timeframe" />
                        <AGSimpleSelect
                            selected={filters.timeframe}
                            onSelect={onSelectTimeframe}
                            options={timeframeOptions}
                            icon={<LinesIcon />}
                            width="170px"
                        />
                    </div>
                    {filters.timeframe === Timeframe.DAILY && (
                        <div>
                            <Label label="Period" />
                            <AGSimpleSelect
                                selected={filters.dailyPeriod}
                                onSelect={onSelectDailyPeriod}
                                options={periodOptions}
                                icon={<LinesIcon />}
                                width="170px"
                            />
                        </div>
                    )}
                    {filters.timeframe === Timeframe.MONTHLY && showYearCompareToggle && (
                        <div className={styles.toggle}>
                            <Toggle checked={isYearToggleOn} onChange={onChangeYearCompareToggle} />
                            <span>Compare Year-over-year</span>
                        </div>
                    )}
                    {filters.timeframe === Timeframe.MONTHLY &&
                        filters.revenueType === RevenueType.NET_REVENUE &&
                        showGuaranteeToggle && (
                            <div className={styles.toggle}>
                                <Toggle checked={isGuaranteeToggleOn} onChange={onChangeGuaranteeCompareToggle} />
                                <span>Compare to Minimum Guarantee</span>
                            </div>
                        )}
                </div>
                {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}
                {filters.timeframe === Timeframe.DAILY ? (
                    <DoDRevenue spot={spot} filters={filters} setErrorMessage={setErrorMessage} />
                ) : (
                    <MoMRevenue
                        spot={spot}
                        filters={filters}
                        setErrorMessage={setErrorMessage}
                        isYearToggleOn={isYearToggleOn}
                        setShowYearCompareToggle={setShowYearCompareToggle}
                        isGuaranteeToggleOn={isGuaranteeToggleOn}
                        setShowGuaranteeToggle={setShowGuaranteeToggle}
                        isAllTime={filters.timeframe === Timeframe.ALL_TIME}
                    />
                )}
            </>
        </WidgetBox>
    );
}

export default MonthlyAndDailyRevenue;
